import { IconName, notEmpty } from '@talos/kyoko';
import { useMemo } from 'react';
import { PANEL_COMPONENTS_MAP } from '../../../components/AppLayout/Panels';
import { BLOTTER_PANEL_COMPONENTS } from '../../../components/AppLayout/Panels/Trading/blotters/blotters';
import { useAppLayoutForRouteParams } from '../../../hooks/useAppLayoutId';
import type { NavigationItem } from '../../../tokens/navigationPaths';
import type { LayoutSpec, PanelType } from '../../../types/LayoutConfig';
import { getTradingLayoutBasePath, getTradingPanelRoute } from './Blotters/navRoutes';

const MARKET_TAB_PANEL_TYPES: PanelType[] = ['marketCards', 'watchlist'];
const BLOTTER_SORT_POSITIONS = new Map(BLOTTER_PANEL_COMPONENTS.map((panel, index) => [panel.type, index]));
const BLOTTER_OVERRIDES: Partial<Record<PanelType, { position: number; icon: IconName }>> = {
  ordersBlotter: { position: -2, icon: IconName.Collection },
  balancesBlotter: { position: -1, icon: IconName.ChartPie075 },
};

export function useMobileTradingNavItems() {
  const currentLayout = useAppLayoutForRouteParams('trading');

  const marketTabs: NavigationItem[] =
    currentLayout == null
      ? []
      : getMarketTabsForLayout(currentLayout).map(panel => ({
          id: panel.id,
          label: panel.title,
          path: getTradingPanelRoute(currentLayout, panel.id),
        }));

  const blotterPanels = useMemo<NavigationItem[]>(() => {
    if (currentLayout == null) {
      return [];
    }

    const sortedBlotterPanels = Object.values(currentLayout.dockViewLayout.panels)
      .map((panel, index) => {
        const panelType = panel.params?.type as PanelType;
        const component = PANEL_COMPONENTS_MAP[panelType];

        if (component?.group?.type !== 'blotters') {
          return undefined;
        }

        const path = getTradingPanelRoute(currentLayout, panel.id);
        return {
          item: {
            id: panel.id,
            label: panel.title,
            icon: (panelType && BLOTTER_OVERRIDES[panelType]?.icon) || component.icon,
            path,
            // Wildcard child so the TabBar matches the blotter when a specific tab (e.g. `/ordersBlotter/open-orders`)
            // is active. The actual tab routing is handled by MobileTradingPanelDispatcher.
            children: [{ path: `${path}/*`, label: undefined }],
          },
          position: panelType
            ? (BLOTTER_OVERRIDES[panelType]?.position ??
              BLOTTER_SORT_POSITIONS.get(panelType) ??
              Number.MAX_SAFE_INTEGER)
            : Number.MAX_SAFE_INTEGER,
          index,
        };
      })
      .filter(notEmpty)
      .toSorted((a, b) => {
        if (a.position !== b.position) {
          return a.position - b.position;
        }
        return a.index - b.index;
      });

    return sortedBlotterPanels.map(({ item }) => item);
  }, [currentLayout]);

  const marketPath = currentLayout ? getTradingLayoutBasePath(currentLayout) : '/trading';

  return [
    {
      id: 'market',
      label: 'Market',
      path: marketPath,
      icon: IconName.ChartBarBox,
      children: marketTabs,
    },
    ...blotterPanels,
  ];
}

function getMarketTabsForLayout(layout: LayoutSpec) {
  const allPanels = Object.values(layout.dockViewLayout.panels);
  return allPanels.filter(panel => MARKET_TAB_PANEL_TYPES.includes(panel.contentComponent as PanelType));
}
